// src/pages/Browser/components/BrowserHome.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaHistory, FaTrash, FaGlobe } from 'react-icons/fa';
import { NetworkContext } from '../../../contexts/NetworkContext';
import './BrowserHome.css';

// IMPORTANT: These are the DEFAULT favorites that should ALWAYS appear
// Modify or add to these lists directly when you want to add new DApps
const DEFAULT_FAVORITES = {
  bitrock: [
    { url: 'https://rock-swap.io', name: 'RockSwap', icon: 'https://rock-swap.io/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' }
  ],
  ethereum: [
    { url: 'https://app.uniswap.org', name: 'Uniswap', icon: 'https://app.uniswap.org/favicon.ico' },
    { url: 'https://etherscan.io', name: 'Etherscan', icon: 'https://etherscan.io/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' },
    // Add new ethereum DApps here
    { url: 'https://app.aave.com', name: 'Aave', icon: 'https://app.aave.com/favicon.ico' }
  ],
  base: [
    { url: 'https://basescan.org', name: 'BaseScan', icon: 'https://basescan.org/favicon.ico' },
    { url: 'https://baseswap.fi', name: 'BaseSwap', icon: 'https://baseswap.fi/_next/image?url=%2Flogo%2FBSWAP.png&w=48&q=75' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' }
  ],
  bsc: [
    { url: 'https://pancakeswap.finance', name: 'PancakeSwap', icon: 'https://pancakeswap.finance/favicon.ico' },
    { url: 'https://four.meme', name: 'Four', icon: 'https://four.meme/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' },
    // Add new BSC DApps here
    { url: 'https://app.1inch.io', name: '1inch', icon: 'https://app.1inch.io/favicon.ico' }
  ],
  arbitrum: [
    { url: 'https://arbiscan.io', name: 'ArbiScan', icon: 'https://arbiscan.io/favicon.ico' },
    { url: 'https://app.camelot.exchange', name: 'Camelot', icon: 'https://app.camelot.exchange/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' }
  ],
  avalanche: [
    { url: 'https://snowtrace.io', name: 'Snowtrace', icon: 'https://snowtrace.io/favicon.ico' },
    { url: 'https://traderjoexyz.com', name: 'TraderJoe', icon: 'https://traderjoexyz.com/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' }
  ],
  optimism: [
    { url: 'https://optimistic.etherscan.io', name: 'OptiScan', icon: 'https://optimistic.etherscan.io/favicon.ico' },
    { url: 'https://app.velodrome.finance', name: 'Velodrome', icon: 'https://app.velodrome.finance/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' }
  ],
  polygon: [
    { url: 'https://quickswap.exchange', name: 'QuickSwap', icon: 'https://quickswap.exchange/favicon.ico' },
    { url: 'https://polygonscan.com', name: 'PolygonScan', icon: 'https://polygonscan.com/favicon.ico' },
    { url: 'https://swap.bit-dog.com', name: 'BitDog MultiChain', icon: 'https://swap.bit-dog.com/bdog-icon.svg' }
  ],
  cronos: [
    { url: 'https://cronos.org/explorer', name: 'Cronos Explorer', icon: 'https://cronos.org/favicon.ico' },
    { url: 'https://mm.finance', name: 'MM Finance', icon: 'https://mm.finance/favicon.ico' }
  ],
  celo: [
    { url: 'https://explorer.celo.org', name: 'Celo Explorer', icon: 'https://explorer.celo.org/favicon.ico' },
    { url: 'https://app.ubeswap.org', name: 'Ubeswap', icon: 'https://app.ubeswap.org/favicon.png' }
  ],
  taraxa: [
    { url: 'https://www.taraswap.app/#/swap', name: 'TaraSwap', icon: 'https://www.taraswap.app/favicon.png' },
    { url: 'https://fomo.biz', name: 'FomoDotBiz', icon: 'https://fomo.biz/assets/pumpeet-BRQsebYm.png' },
  ],
  infinaeon: [
    { url: 'https://lunarpump.fun', name: 'LunarPump', icon: 'https://lunarpump.fun/favicon.ico' },
    { url: 'https://bridge.infinaeon.com', name: 'Infinaeon Bridge', icon: 'https://bridge.infinaeon.com/favicon.ico' },
    { url: 'https://infinityswap.finance', name: 'InfinitySwap', icon: 'https://infinityswap.finance/favicon.ico' },
    { url: 'https://explorer.infinaeon.com', name: 'Infinaeon Explorer', icon: 'https://explorer.infinaeon.com/favicon.ico' }
  ],
  omax: [
    { url: 'https://omaxscan.com', name: 'Omax Explorer', icon: 'https://omaxscan.com/favicon.ico' }
  ]
};

// This constant indicates which favorites are custom (user-added)
const CUSTOM_FAVORITES_KEY = 'bitdog_browser_custom_favorites';

const BrowserHome = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { currentChain } = useContext(NetworkContext);
  const [customFavorites, setCustomFavorites] = useState({});
  const [currentFavorites, setCurrentFavorites] = useState([]);
  const [history, setHistory] = useState([]);
  const [newFavoriteUrl, setNewFavoriteUrl] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Load custom favorites and history from local storage
  useEffect(() => {
    try {
      // Load custom favorites (user-added)
      const savedCustomFavorites = localStorage.getItem(CUSTOM_FAVORITES_KEY);
      if (savedCustomFavorites) {
        setCustomFavorites(JSON.parse(savedCustomFavorites));
      }
      
      // Load history
      const savedHistory = localStorage.getItem('bitdog_browser_history');
      if (savedHistory) {
        setHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Error loading browser data:', error);
    }
  }, []);
  
  // Update current favorites when chain changes, combining defaults and custom
  useEffect(() => {
    if (currentChain && currentChain.key) {
      console.log(`Updating favorites for chain: ${currentChain.key}`);
      
      // Get default favorites for this chain
      const defaultChainFavorites = DEFAULT_FAVORITES[currentChain.key] || [];
      
      // Get custom favorites for this chain
      const customChainFavorites = customFavorites[currentChain.key] || [];
      
      // Combine both (default first, then custom)
      const combinedFavorites = [...defaultChainFavorites, ...customChainFavorites];
      
      console.log(`Found ${combinedFavorites.length} favorites for chain ${currentChain.key}`);
      console.log(`Default: ${defaultChainFavorites.length}, Custom: ${customChainFavorites.length}`);
      
      // Update state with combined favorites
      setCurrentFavorites(combinedFavorites);
    } else {
      console.log("No current chain", { currentChain });
      setCurrentFavorites([]);
    }
  }, [currentChain, customFavorites]);
  
  // Function to update custom favorites in localStorage
  const updateCustomFavoritesStorage = (newCustomFavorites) => {
    try {
      localStorage.setItem(CUSTOM_FAVORITES_KEY, JSON.stringify(newCustomFavorites));
      return newCustomFavorites;
    } catch (error) {
      console.error('Error updating custom favorites in storage:', error);
      return newCustomFavorites;
    }
  };
  
  const addFavorite = () => {
    if (!newFavoriteUrl || !currentChain || !currentChain.key) {
      console.error("Cannot add favorite: missing URL or current chain", { newFavoriteUrl, currentChain });
      return;
    }
    
    let url = newFavoriteUrl;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    try {
      const hostname = new URL(url).hostname;
      const newFavorite = {
        url,
        name: hostname,
        icon: `https://${hostname}/favicon.ico`
      };
      
      // Update custom favorites only (not defaults)
      const updatedCustomFavorites = { ...customFavorites };
      
      // Initialize chain favorites if it doesn't exist
      if (!updatedCustomFavorites[currentChain.key]) {
        updatedCustomFavorites[currentChain.key] = [];
      }
      
      // Add favorite to current chain's custom list
      updatedCustomFavorites[currentChain.key].push(newFavorite);
      
      // Update localStorage and state
      updateCustomFavoritesStorage(updatedCustomFavorites);
      setCustomFavorites(updatedCustomFavorites);
      
      // Clear the input and hide the form
      setNewFavoriteUrl('');
      setShowAddForm(false);
      
      console.log("Added custom favorite:", newFavorite);
    } catch (error) {
      console.error('Invalid URL:', error);
    }
  };
  
  const removeFavorite = (index, event) => {
    event.stopPropagation();
    
    if (!currentChain || !currentChain.key) {
      console.error("Cannot remove favorite: missing current chain");
      return;
    }
    
    // Check if this is a default favorite or a custom favorite
    const defaultCount = (DEFAULT_FAVORITES[currentChain.key] || []).length;
    
    if (index < defaultCount) {
      // This is a default favorite - we don't remove these
      console.log("Cannot remove default favorite at index:", index);
      return;
    }
    
    // This is a custom favorite - get its actual index in the custom array
    const customIndex = index - defaultCount;
    
    // Update custom favorites
    const updatedCustomFavorites = { ...customFavorites };
    
    if (updatedCustomFavorites[currentChain.key]) {
      updatedCustomFavorites[currentChain.key] = updatedCustomFavorites[currentChain.key].filter((_, i) => i !== customIndex);
      
      // Update localStorage and state
      updateCustomFavoritesStorage(updatedCustomFavorites);
      setCustomFavorites(updatedCustomFavorites);
      
      console.log("Removed custom favorite at index:", customIndex);
    }
  };
  
  // Improved handler for touch events
  const handleDeleteButtonTouch = (index, event) => {
    // Prevent the card click from triggering navigation
    event.stopPropagation();
    event.preventDefault();
    
    // Call the remove favorite function
    removeFavorite(index, event);
  };
  
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('bitdog_browser_history');
  };
  
  const navigate = (url) => {
    if (onNavigate) {
      onNavigate(url);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      addFavorite();
    } else if (e.key === 'Escape') {
      setShowAddForm(false);
    }
  };
  
  return (
    <div className="browser-home">
      <section className="browser-section">
        <h2 className="section-title">
          {currentChain ? `${currentChain.name} ${t('browser.favorites')}` : t('browser.favorites')}
        </h2>
        
        <div className="dapp-grid">
          {currentFavorites && currentFavorites.map((favorite, index) => {
            // Determine if this is a default favorite
            const defaultCount = (DEFAULT_FAVORITES[currentChain?.key] || []).length;
            const isDefault = index < defaultCount;
            
            return (
              <div 
                key={`${favorite.url}-${index}`} 
                className={`dapp-card ${isDefault ? 'default-favorite' : 'custom-favorite'}`} 
                onClick={() => navigate(favorite.url)}
              >
                <div className="dapp-icon">
                  <img 
                    src={favorite.icon} 
                    alt={favorite.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/wallet-logo.png';
                    }}
                  />
                </div>
                <div className="dapp-name">{favorite.name}</div>
                {!isDefault && (
                  <button 
                    className="remove-button"
                    onClick={(e) => removeFavorite(index, e)}
                    onTouchStart={(e) => handleDeleteButtonTouch(index, e)}
                    aria-label={t('actions.remove')}
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            );
          })}
          
          {/* Add favorite card */}
          <div className="dapp-card add-card" onClick={() => setShowAddForm(true)}>
            <div className="add-icon">
              <FaPlus />
            </div>
            <div className="dapp-name">{t('actions.add')}</div>
          </div>
        </div>
        
        {showAddForm && (
          <div className="add-favorite-form">
            <input
              type="text"
              value={newFavoriteUrl}
              onChange={(e) => setNewFavoriteUrl(e.target.value)}
              placeholder={t('browser.enterUrl')}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <div className="add-form-buttons">
              <button onClick={() => setShowAddForm(false)} className="cancel-button">
                {t('actions.cancel')}
              </button>
              <button onClick={addFavorite} className="add-button">
                {t('actions.add')}
              </button>
            </div>
          </div>
        )}
      </section>
      
      <section className="browser-section">
        <div className="section-header">
          <h2 className="section-title">{t('browser.recentlyVisited')}</h2>
          {history.length > 0 && (
            <button 
              className="clear-button"
              onClick={clearHistory}
              aria-label={t('browser.clearHistory')}
            >
              <FaTrash />
            </button>
          )}
        </div>
        
        {history.length > 0 ? (
          <div className="history-list">
            {history.map((item, index) => (
              <div key={index} className="history-item" onClick={() => navigate(item.url)}>
                <div className="history-icon">
                  <img 
                    src={`https://${new URL(item.url).hostname}/favicon.ico`}
                    alt=""
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/wallet-logo.png';
                    }}
                  />
                </div>
                <div className="history-info">
                  <div className="history-title">{item.title || new URL(item.url).hostname}</div>
                  <div className="history-url">{item.url}</div>
                </div>
                <div className="history-time">{item.time}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FaHistory className="empty-icon" />
            <p>{t('browser.noHistory')}</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default BrowserHome;